import React, { MouseEvent } from "react";
import PlacesAutocomplete, { getLatLng } from "react-places-autocomplete";

import {
  GEO_COORDINATES_FROM_SEARCH,
  SEARCH_FORM_ATMS,
  SEARCH_FORM_BRANCHES,
  TabType,
} from "@constants";
import { getLocalization, geocodeByAddressCZ } from "@utilities";

import { LocalizationProps } from "../types";

type SearchFormBranchesAtmsProps = {
  type: TabType;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setShowAlertNoResult: React.Dispatch<React.SetStateAction<boolean>>;
  isToggled: boolean;
} & LocalizationProps;

const LogoPoweredByGoogle = () => {
  return (
    <div className="relative h-[22px] w-full border border-t-0 border-grey-30 bg-white">
      <div className="absolute right-1 top-1">
        <img
          alt="Powered by Google"
          src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png"
          width="120px"
          height="140px"
        />
      </div>
    </div>
  );
};

const SearchFormBranchesAtms: React.FC<SearchFormBranchesAtmsProps> = ({
  type,
  inputValue,
  setInputValue,
  setShowAlertNoResult,
  localization,
  isToggled,
}) => {
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);

  // search
  const searchPlaceholder = getLocalization(localization, "Branches_SearchPlaceholder");
  const fetchSuggestions = inputValue.length > 2;
  const searchOptions = {
    componentRestrictions: { country: "cz" },
  };

  const getFormClass = () => {
    return type === TabType.Branches ? SEARCH_FORM_BRANCHES : SEARCH_FORM_ATMS;
  };

  const handleChangeAddress = (value) => {
    setInputValue(value);
    if (value === "") {
      setShowAlertNoResult(false);
    }
  };

  // event is not trusted
  // clicks submit button programmatically
  const handleSelectAddress = async (value) => {
    window.location.hash = "#ResultsList";

    // takes address and changes it to geolocation coordinates
    let results;
    let latLng;
    try {
      results = await geocodeByAddressCZ(value);
      latLng = await getLatLng(results[0]);
    } catch (error) {
      console.warn(error);
      setShowAlertNoResult(true);
      return;
    }
    // coordinates are stored in local storage
    window.localStorage.setItem(GEO_COORDINATES_FROM_SEARCH, JSON.stringify(latLng));

    // set state
    setShowAlertNoResult(false);
    setInputValue(value);

    // get submit button and click on it
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  // event is trusted - submit clicked by USER
  // used to recognize 'handleSelectAddress' vs real button click by user
  // https://developer.mozilla.org/en-US/docs/Web/API/Event/isTrusted
  const handleSubmitOnCLick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    const isClickedByUser = event.isTrusted;
    window.location.hash = "#ResultsList";

    if (isClickedByUser) {
      event.preventDefault();
      let results;
      let latLng;
      try {
        results = await geocodeByAddressCZ(inputValue);
        latLng = await getLatLng(results[0]);
      } catch (error) {
        console.warn(error);
        setShowAlertNoResult(true);
        return;
      }
      // coordinates are stored in local storage
      window.localStorage.setItem(GEO_COORDINATES_FROM_SEARCH, JSON.stringify(latLng));

      // set state
      setShowAlertNoResult(false);

      // get submit button and click on it
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    }
  };

  // Log error status and clear dropdown when Google Maps API returns an error.
  const onError = (status, clearSuggestions) => {
    if (!isProduction) {
      console.warn("Google Maps API returned status: ", status);
    }
    if (status === "ZERO_RESULTS" || status === "NOT_FOUND") {
      setShowAlertNoResult(true);
    }
    clearSuggestions();
  };

  return (
    <div className="search-ba">
      <form className={`${getFormClass()} search-ba__form`} method="POST" noValidate>
        <label className="sr-only" htmlFor="query">
          {searchPlaceholder}
        </label>
        <div className="autocomplete-wrapper">
          <PlacesAutocomplete
            value={inputValue}
            onChange={handleChangeAddress}
            onSelect={handleSelectAddress}
            searchOptions={searchOptions}
            shouldFetchSuggestions={fetchSuggestions}
            onError={onError}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps }) => (
              <div className="autocomplete-root">
                <input
                  {...getInputProps({ placeholder: searchPlaceholder })}
                  type="text"
                  name="query"
                  id="query"
                  autoComplete="off"
                  data-testid={`search-input-${type}`}
                  className="autocomplete-input"
                />

                {isToggled && (
                  <button
                    type="button"
                    id={`btn-${type}-localization`}
                    className="absolute top-3 right-4"
                  >
                    <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11.249 3.784a8.263 8.263 0 0 0-7.466 7.466h-2.53v1.5h2.53a8.263 8.263 0 0 0 7.466 7.466v2.53h1.5v-2.53a8.263 8.263 0 0 0 7.466-7.466h2.533v-1.5h-2.533a8.263 8.263 0 0 0-7.466-7.466v-2.53h-1.5v2.53Zm.75 1.466c-3.72 0-6.75 3.03-6.75 6.75s3.03 6.75 6.75 6.75 6.75-3.03 6.75-6.75-3.03-6.75-6.75-6.75Zm0 10.5c-2.07 0-3.75-1.68-3.75-3.75 0-2.07 1.68-3.75 3.75-3.75 2.07 0 3.75 1.68 3.75 3.75 0 2.07-1.68 3.75-3.75 3.75Zm0-6c-1.24 0-2.25 1.01-2.25 2.25s1.01 2.25 2.25 2.25 2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25Z"
                        fill="#E00000"
                      />
                    </svg>
                  </button>
                )}
                <div className="autocomplete-dropdown-container">
                  {suggestions.map((suggestion) => {
                    const style = {
                      backgroundColor: suggestion.active ? "#EAEEF3" : "#FFFFFF",
                    };

                    return (
                      <div
                        {...getSuggestionItemProps(suggestion, { style })}
                        key={suggestion.placeId}
                        className="autocomplete-suggestion"
                      >
                        {suggestion.description}
                      </div>
                    );
                  })}

                  {suggestions.length > 0 && <LogoPoweredByGoogle />}
                </div>
              </div>
            )}
          </PlacesAutocomplete>
        </div>
        <button
          ref={submitButtonRef}
          type="submit"
          className="search-ba__form-submit-button"
          data-testid={`search-button-${type}`}
          onClick={handleSubmitOnCLick}
        >
          {getLocalization(localization, "SearchButton")}
        </button>
      </form>
    </div>
  );
};

export default SearchFormBranchesAtms;
